import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import searchSlice from '~/redux/slices/search';
import { useRouting } from '~/routing/useRouting';
import { redirectToPage } from '~/services/redirects';

import { buildClauseAnalyzerParams, calculateF1Score } from '../../util';

const PredictiveInfo = ({
  onDeleteGuidedAi,
  onDownloadSamples,
  onRetrainGuidedAi,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const subdomain = useClientSubDomain();
  const { navigate } = useRouting();
  const model = useSelector(slices.automation.selectors.selectAutomationModel);
  const hasAdvancedSearch = useHasFeatureFlag(FeatureFlagType.SearchV2);

  const f1Score = calculateF1Score(model?.current?.accuracyMetrics) || 0;

  const { data: sectionsAndFolders, isFetching: isFetchingSections } =
    api.endpoints.getFilterSections.useQuery(undefined);

  const { data: idf, isFetching: isFetchingIdf } =
    api.endpoints.getIdentifications.useQuery(
      { name: model.name },
      { skip: !model.name },
    );

  const redirectToAnalyzerResults = ({ name }) => {
    const params = new URLSearchParams(
      buildClauseAnalyzerParams(name),
    ).toString();
    redirectToPage(subdomain, `analyzer/results?${params}`);
  };

  const redirectToAdvancedSearch = ({ name }) => {
    const values = [{ provision: name, text_search: [] }];
    const filters = [
      { id: uuid(), fieldId: 'clause', operatorId: 'contains_any', values },
    ];
    const fields = sectionsAndFolders?.fields;
    dispatch(searchSlice.actions.setFilters(filters));
    dispatch(
      searchSlice.actions.setQuery({ booleanQuery: '', filters, fields }),
    );
    navigate('/search');
  };

  const redirectToSearchResults = ({ name }) => {
    if (hasAdvancedSearch) {
      redirectToAdvancedSearch({ name });
    } else {
      redirectToAnalyzerResults({ name });
    }
  };

  const onActionsMenu = (v) => {
    switch (v) {
      case 'RETRAIN_MODEL':
        onRetrainGuidedAi();
        break;
      case 'DOWNLOAD_CSV':
        onDownloadSamples();
        break;
      case 'DELETE_MODEL':
        onDeleteGuidedAi();
        break;
      default:
        return;
    }
  };

  return (
    <Layout
      direction="column"
      border="border.divider"
      borderRadius="m"
      mb={4}
      p={4}
      spacing={4}
    >
      <Text variant="body-bold">
        {intl.formatMessage({
          id: 'PredictiveInfo.title',
          defaultMessage: 'Predictive Info',
          description: 'Title for predictive info section',
        })}
      </Text>
      <Layout>
        <Layout direction="column">
          <Text>
            {intl.formatMessage({
              id: 'PredictiveInfo.identificationsFoundLabel',
              defaultMessage: 'Identifications Found',
              description: 'Label for identifications found',
            })}
          </Text>
          <Text variant="title">
            {idf?.smartClauseTotalIdentifications ?? 0}
          </Text>
        </Layout>
        <Layout direction="column" pl={8}>
          <Text>
            {intl.formatMessage({
              id: 'PredictiveInfo.f1ScoreLabel',
              defaultMessage: 'F1 Score',
              description: 'Label for F1 score',
            })}
          </Text>
          <Text variant="title">{f1Score}</Text>
        </Layout>
      </Layout>
      <Layout spacing={2} justify="space-between">
        <Button
          text={intl.formatMessage({
            id: 'PredictiveInfo.viewIdentificationsButton',
            defaultMessage: 'View Identifications',
            description: 'Button text for viewing identifications',
          })}
          variant="primary"
          onClick={() => redirectToSearchResults({ name: model?.name })}
          isLoading={isFetchingSections || isFetchingIdf}
          disable={(idf?.smartClauseTotalIdentifications ?? 0) === 0}
        />
        <ActionsMenu
          name={intl.formatMessage({
            id: 'PredictiveInfo.moreActionsMenu',
            defaultMessage: 'more actions',
            description: 'Name for more actions menu',
          })}
          actions={[
            {
              label: intl.formatMessage({
                id: 'PredictiveInfo.retrainModelAction',
                defaultMessage: 'Retrain model',
                description: 'Action label for retraining model',
              }),
              value: 'RETRAIN_MODEL',
            },
            {
              label: intl.formatMessage({
                id: 'PredictiveInfo.downloadTrainingDataAction',
                defaultMessage: 'Download training data',
                description: 'Action label for downloading training data',
              }),
              value: 'DOWNLOAD_CSV',
            },
            {
              label: intl.formatMessage({
                id: 'PredictiveInfo.deleteModelAction',
                defaultMessage: 'Delete model',
                description: 'Action label for deleting model',
              }),
              value: 'DELETE_MODEL',
            },
          ]}
          onActionClick={onActionsMenu}
        />
      </Layout>
    </Layout>
  );
};

export default PredictiveInfo;
